// FNP-1191 — mirrors the FNP-1184 neutral read schema
// (flexi-domains-api/src/appsync-api/src/schema/features/dns-zones.graphql).
//
//   listDnsZones(connectionId: ID, limit: Int, nextToken: String): DnsZonesConnection
//   getDnsZone(connectionId: ID!, id: ID!): DnsZone
//
// Three rules travel with these types and are enforced by the code below them:
//
//  1. `DnsZone.id` is OPAQUE — Flexi's own id, minted by the adapter, never the
//     provider's. Nothing in this module parses it, splits it or renders it as
//     "the Route 53 zone id". It is passed back verbatim.
//  2. `nextToken != null` is the ONLY end-of-results signal. `limit` applies
//     BEFORE filtering, so an EMPTY page with a live token is normal.
//  3. `getDnsZone` needs BOTH connectionId and id — a zone id is only
//     meaningful against the connection that minted it.

export type DnsProviderKind = 'ROUTE53' | 'CLOUDFLARE' | 'FAKE';

export type DnsZone = {
    id: string,
    name: string,
    connectionId: string,
    provider: DnsProviderKind,
    isPrivate: boolean,
    nameservers: string[],
    // ⚠️ TRI-STATE, and the third state is not cosmetic.
    //
    //   true  — the registrar-side nameservers appear to match this provider's
    //   false — determined, and they do NOT appear to match
    //   null  — NOT DETERMINED (undefined here when the field is absent)
    //
    // Null and false are DIFFERENT. Collapsing them lets the UI report a
    // healthy zone as broken in the minutes after a delegation change, which is
    // exactly what the schema comment warns against. Everything that renders or
    // filters this field branches on all three — see delegationState() below.
    appearsDelegated?: boolean | null,
}

// ⚠️ WHY THIS IS NOT A PLAIN CONNECTION TYPE.
//
// Zones fan out across EVERY connection a tenant has. When one connection is
// broken, the honest answer is not a shorter list: a tenant with three
// connections where one is down must not be shown two connections' zones
// silently, which is indistinguishable from having deleted them.
//
// So `failures` carries the connections that could not be read, and the module
// renders BOTH — the zones that loaded AND a banner naming what did not.
// Partial success is success-with-a-warning, never an error state.
export type DnsZonesConnectionResult = {
    items: DnsZone[],
    nextToken?: string | null,
    // `[DnsConnectionFailure!]!` — EMPTY, never null. Check `.length`.
    failures: DnsConnectionFailure[],
}

export type DnsConnectionFailure = {
    connectionId: string,
    provider: DnsProviderKind,
    // The normalised failure class, e.g. "AUTHENTICATION_FAILED".
    kind: string,
    // The provider's own message, VERBATIM. Never re-worded on the way to the
    // screen — classify for the core, preserve for the human (FNP-1180).
    message: string,
    // A rate limit is retryable; a revoked token is not, and the tenant must go
    // fix something. The banner says which.
    retryable: boolean,
}

export type ListDnsZones = {
    listDnsZones: DnsZonesConnectionResult
}

export type GetDnsZone = {
    getDnsZone: DnsZone
}

// THE TRI-STATE, NAMED.
//
// A string union rather than `boolean | null | undefined` scattered through the
// column, the profile and the filter. Three call sites branching on nullability
// independently is how two of them end up agreeing and the third does not.
export type DelegationState = 'DELEGATED' | 'NOT_DELEGATED' | 'UNDETERMINED';

export const delegationState = (zone: DnsZone): DelegationState => {
    if (zone.appearsDelegated === true) {
        return 'DELEGATED';
    }
    if (zone.appearsDelegated === false) {
        return 'NOT_DELEGATED';
    }
    // null AND undefined land here. "The API did not say" — NOT "not delegated".
    return 'UNDETERMINED';
};

// What a connection's credential proved it could do (FNP-1181
// `ProviderConnectionCapabilities`). The catalog entry is the CEILING; this is
// that ceiling narrowed by the credential — a tenant who handed Flexi a
// read-only Route 53 role has a connection that cannot write.
export type ZoneWriteCapabilities = {
    canWriteRecords: boolean,
    canCreateZones: boolean,
    canDeleteZones: boolean,
    canSetNameservers: boolean,
}
